import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Input, Modal, Skeleton, Tag, Typography } from 'antd';
import type { BankAccountDirectoryEntry } from '@youkuai/contracts';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { exportBankAccount, getBankAccount, listBankAccounts, saveBankAccount } from './bank-account-api.ts';
import { ApiRequestError } from './project-api.ts';

const { Text, Title } = Typography;

function issueText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号的查看、修改或导出权已变化。',
      VERSION_CONFLICT: '账户已被他人修改，请刷新后再试。',
      EXPORT_TICKET_EXPIRED: '导出票据已过期，请重新填写用途。',
      EXPORT_SOURCE_CHANGED: '账户已变化，旧票据不再可下载。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '本次保存标识已用于其他内容，请重新提交。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

export function BankAccountsPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const userId = state.kind === 'ready' ? state.session.userId : '';
  const isAdministration = state.kind === 'ready' && state.session.roles.includes('ADMINISTRATION');
  const canList = state.kind === 'ready' && state.session.roles.some((role) => role === 'ADMIN' || role === 'ADMINISTRATION');
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selected, setSelected] = useState<BankAccountDirectoryEntry | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [accountHolder, setAccountHolder] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankLocation, setBankLocation] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [reason, setReason] = useState('');
  const [exportPurpose, setExportPurpose] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const accounts = useInfiniteQuery({ queryKey: ['bank-account-directory', userId, activeSearch],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listBankAccounts(token, { search: activeSearch,
      ...(pageParam ? { afterCode: pageParam } : {}) }),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && canList });
  const rows = accounts.data?.pages.flatMap((page) => page.items) ?? [];
  const detail = useQuery({ queryKey: ['bank-account', selected?.payeeId, selected?.version],
    queryFn: () => getBankAccount(token, selected!.payeeId),
    enabled: !!token && !!selected?.hasAccount, retry: false });
  const saveMutation = useMutation({ mutationFn: saveBankAccount });
  const exportMutation = useMutation({ mutationFn: exportBankAccount });

  function select(row: BankAccountDirectoryEntry) {
    setSelected(row); setEditorOpen(false); setExportOpen(false); setNotice(null);
  }

  function edit() {
    if (!selected || !isAdministration) return;
    setAccountHolder(detail.data?.visibility === 'FULL' ? detail.data.accountHolder : '');
    setBankName(detail.data?.visibility === 'FULL' ? detail.data.bankName : '');
    setAccountNumber(detail.data?.visibility === 'FULL' ? detail.data.accountNumber : '');
    setBankLocation(detail.data?.visibility === 'FULL' ? detail.data.bankLocation ?? '' : '');
    setPaymentNote(detail.data?.visibility === 'FULL' ? detail.data.paymentNote ?? '' : '');
    setReason(''); setEditorOpen(true); setNotice(null);
  }

  async function save() {
    if (!selected) return;
    try {
      await saveMutation.mutateAsync({ token, payeeId: selected.payeeId, expectedVersion: selected.version,
        accountHolder, bankName, accountNumber, bankLocation, paymentNote, reason,
        idempotencyKey: `bank-account-${crypto.randomUUID()}` });
      setEditorOpen(false); setNotice({ type: 'success', text: '收款账户已加密保存，本次变更已记录审计。' });
      await client.invalidateQueries({ queryKey: ['bank-account-directory', userId] });
      await client.invalidateQueries({ queryKey: ['bank-account', selected.payeeId] });
      setSelected(null);
    } catch (error) { setNotice({ type: 'error', text: issueText(error) }); }
  }

  async function download() {
    if (!selected) return;
    try {
      const result = await exportMutation.mutateAsync({ token, payeeId: selected.payeeId, purpose: exportPurpose });
      const url = URL.createObjectURL(result.file);
      const anchor = document.createElement('a'); anchor.href = url;
      anchor.download = `bank-account-${selected.personCode}.csv`; anchor.click(); URL.revokeObjectURL(url);
      setExportOpen(false); setExportPurpose('');
      setNotice({ type: 'success', text: '已生成带导出人、时间和用途水印的文件。' });
    } catch (error) { setNotice({ type: 'error', text: issueText(error) }); }
  }

  return <main className="page-shell bank-account-shell">
    <Link className="back-link" to="/projects">← 返回我的项目</Link>
    <div className="section-kicker">SECURITY / 字段级权限</div>
    <div className="project-heading"><div><Title level={1}>收款账户管理</Title>
      <p>账户名、开户行和账号加密存储；行政可查看和维护，普通管理员仅见脱敏账号。</p></div></div>
    {!canList ? <Alert type="error" showIcon title="当前账号无权查看收款账户目录" /> : <>
      {notice && <Alert className="workspace-alert" showIcon type={notice.type} title={notice.text} closable
        onClose={() => setNotice(null)} />}
      <Alert className="workspace-alert" type="info" showIcon title="明文查看会记录审计"
        description="导出还需单独授予“银行账户”导出权，并在每次导出时填写用途。" />
      <Input.Search value={search} onChange={(event) => setSearch(event.target.value)}
        onSearch={() => setActiveSearch(search)} allowClear placeholder="按人员编号或姓名查找"
        enterButton="查找收款对象" className="project-search" />
      {accounts.isPending && <Skeleton active paragraph={{ rows: 6 }} />}
      {accounts.isError && <Alert type="error" showIcon title="收款账户目录读取失败"
        description={issueText(accounts.error)} />}
      {!accounts.isPending && !accounts.isError && rows.length === 0 && <Empty description="没有匹配的有效收款对象" />}
      <div className="bank-account-workbench">
        <div className="bank-account-directory">{rows.map((row) => <button type="button"
          className={`bank-account-row${selected?.payeeId === row.payeeId ? ' is-active' : ''}`}
          key={row.payeeId} onClick={() => select(row)}>
          <span><strong>{row.displayName}</strong><small>{row.personCode}</small></span>
          <span><Tag color={row.hasAccount ? 'success' : 'warning'}>{row.hasAccount ? row.maskedAccountNumber : '待录入'}</Tag></span>
        </button>)}</div>
        <Card className="bank-account-detail" variant="borderless">
          {!selected && <Empty description="选择一位收款对象查看账户状态" />}
          {selected && !selected.hasAccount && <>
            <Text type="secondary">{selected.personCode}</Text><Title level={3}>{selected.displayName}</Title>
            <Alert type="warning" showIcon title="尚未录入收款账户" />
            {isAdministration && <Button type="primary" onClick={edit}>录入账户</Button>}
          </>}
          {selected?.hasAccount && detail.isPending && <Skeleton active />}
          {selected?.hasAccount && detail.isError && <Alert type="error" showIcon title="账户明细读取失败"
            description={issueText(detail.error)} />}
          {selected?.hasAccount && detail.data && <>
            <Flex justify="space-between" align="start" gap={16} wrap><div><Text type="secondary">{detail.data.personCode}</Text>
              <Title level={3}>{detail.data.displayName}</Title></div>
              <Tag color={detail.data.visibility === 'FULL' ? 'processing' : 'default'}>
                {detail.data.visibility === 'FULL' ? '明文权限' : '脱敏权限'}</Tag></Flex>
            <dl className="status-list">
              <div><dt>账户名</dt><dd>{detail.data.accountHolder ?? '仅行政和本人可见'}</dd></div>
              <div><dt>开户行</dt><dd>{detail.data.bankName ?? '仅行政和本人可见'}</dd></div>
              <div><dt>开户地</dt><dd>{detail.data.bankLocation ?? '尚未填写'}</dd></div>
              <div><dt>银行账号</dt><dd className="account-number">{detail.data.accountNumber ?? detail.data.maskedAccountNumber}</dd></div>
              <div><dt>收款备注</dt><dd>{detail.data.paymentNote ?? '尚未填写'}</dd></div>
              <div><dt>数据版本</dt><dd>v{detail.data.version}</dd></div>
              <div><dt>最后更新</dt><dd>{new Date(detail.data.updatedAt).toLocaleString('zh-CN')}</dd></div>
            </dl>
            {detail.data.visibility === 'FULL' && <Flex gap={10} wrap>
              {isAdministration && <Button type="primary" onClick={edit}>修改账户</Button>}
              <Button onClick={() => { setExportPurpose(''); setExportOpen(true); }}>填写用途并导出</Button>
            </Flex>}
          </>}
        </Card>
      </div>
      {accounts.hasNextPage && <Button loading={accounts.isFetchingNextPage} onClick={() => void accounts.fetchNextPage()}>
        继续加载</Button>}
    </>}
    <Modal title={selected?.hasAccount ? '修改收款账户' : '录入收款账户'} open={editorOpen} footer={null}
      destroyOnHidden onCancel={() => !saveMutation.isPending && setEditorOpen(false)}>
      <Alert type="warning" showIcon title="确认人员身份后再录入" description="保存后将加密存储，修改原因会进入审计记录。" />
      <div className="bank-account-form">
        <label>账户名<Input value={accountHolder} maxLength={120} onChange={(event) => setAccountHolder(event.target.value)} /></label>
        <label>开户行<Input value={bankName} maxLength={160} onChange={(event) => setBankName(event.target.value)} /></label>
        <label>开户地<Input value={bankLocation} maxLength={160} onChange={(event) => setBankLocation(event.target.value)} /></label>
        <label>银行账号<Input value={accountNumber} maxLength={64} autoComplete="off"
          onChange={(event) => setAccountNumber(event.target.value)} /></label>
        <label>收款备注<Input.TextArea value={paymentNote} maxLength={500} rows={2}
          onChange={(event) => setPaymentNote(event.target.value)} /></label>
        <label>修改原因（至少 10 字）<Input.TextArea value={reason} maxLength={500} rows={3}
          onChange={(event) => setReason(event.target.value)} /></label>
      </div>
      <Flex justify="end" gap={10}><Button onClick={() => setEditorOpen(false)}>取消</Button>
        <Button type="primary" loading={saveMutation.isPending}
          disabled={!accountHolder.trim() || !bankName.trim() || accountNumber.replace(/[\s-]+/gu, '').length < 8 || reason.trim().length < 10}
          onClick={() => void save()}>加密保存</Button></Flex>
    </Modal>
    <Modal title="导出银行账户" open={exportOpen} footer={null} destroyOnHidden
      onCancel={() => !exportMutation.isPending && setExportOpen(false)}>
      <Alert type="info" showIcon title="导出银行账户需独立权限"
        description="系统将记录导出人、时间、用途和文件范围，下载票据 5 分钟后失效。" />
      <p>导出用途（至少 10 字）：</p>
      <Input.TextArea value={exportPurpose} maxLength={500} rows={3}
        onChange={(event) => setExportPurpose(event.target.value)} />
      <Flex justify="end" gap={10} className="customer-modal-actions"><Button onClick={() => setExportOpen(false)}>取消</Button>
        <Button type="primary" loading={exportMutation.isPending} disabled={exportPurpose.trim().length < 10}
          onClick={() => void download()}>确认并下载 1 条</Button></Flex>
    </Modal>
  </main>;
}
